import { Injectable, inject, signal, computed } from "@angular/core";
import { Router, ActivatedRoute } from "@angular/router";
import { injectMutation, injectQueryClient } from "@ngneat/query";
import { AuthService } from "./auth.service";
import { AccountSummary, AuthResponse } from "../models/auth.model";
import { SettingsService } from "../../../core/services/settings.service";
import { SETTINGS_KEYS } from "../../../core/constants/settings-keys";
import { TenantUrlUtilService } from "@shared/services/tenant-url-util.service";

@Injectable({ providedIn: "root" })
export class AuthState {
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly settings = inject(SettingsService);
  private readonly tenantUrlUtil = inject(TenantUrlUtilService);
  private readonly mutation = injectMutation();
  private readonly queryClient = injectQueryClient();

  readonly account = signal<AccountSummary | null>(null);
  readonly isAuthenticated = signal<boolean>(false);
  readonly error = signal<string | null>(null);

  readonly hasPermissions = computed(() => !!this.account()?.permissions);
  readonly userEmail = computed(() => this.account()?.email ?? "");
  readonly userName = computed(
    () => this.account()?.firstName || this.account()?.name || "",
  );

  readonly loginMutation = this.mutation({
    mutationFn: async (credentials: { email: string; password: string }) =>
      await this.authService.loginWithEmail(
        credentials.email,
        credentials.password,
      ),
    onSuccess: (response: AuthResponse) => {
      this.handleAuthSuccess(response);
    },
    onError: (error: any) => {
      this.handleAuthError(error);
    },
  });

  readonly loginWithMicrosoftMutation = this.mutation({
    mutationFn: async () => await this.authService.loginWithMicrosoft(),
    onSuccess: (response: AuthResponse) => {
      this.handleAuthSuccess(response);
    },
    onError: (error: any) => {
      this.handleAuthError(error);
    },
  });

  readonly logoutMutation = this.mutation({
    mutationFn: async () => await this.authService.logout(),
    onSuccess: () => {
      this.clearAuthData();
      this.router.navigateByUrl("/login", { replaceUrl: true });
    },
    onError: () => {
      this.clearAuthData();
      this.router.navigateByUrl("/login", { replaceUrl: true });
    },
  });

  readonly forgotPasswordMutation = this.mutation({
    mutationFn: async (email: string) =>
      await this.authService.sendPasswordResetEmail(email),
    onSuccess: () => {
      this.error.set(null);
      console.log("Password reset email sent successfully");
    },
    onError: (error: any) => {
      const errorMessage = this.getPasswordResetErrorMessage(error);
      this.error.set(errorMessage);
    },
  });

  constructor() {
    this.loadAuthData();
  }

  login(email: string, password: string): void {
    this.error.set(null);
    this.loginMutation.reset();
    this.loginMutation.mutate({ email, password });
  }

  loginWithMicrosoft(): void {
    this.error.set(null);
    this.loginWithMicrosoftMutation.mutate({});
  }

  logout(): void {
    this.loginMutation.reset();
    this.loginWithMicrosoftMutation.reset();
    this.forgotPasswordMutation.reset();
    this.logoutMutation.mutate({});
  }

  sendPasswordResetEmail(email: string): void {
    this.error.set(null);
    this.forgotPasswordMutation.reset();
    this.forgotPasswordMutation.mutate(email);
  }

  private handleAuthSuccess(response: AuthResponse): void {
    if (!response.account.permissions) {
      this.error.set("No estás asignado/a a ningún equipo");
      return;
    }
    this.saveAuthData(response.account);
    this.account.set(response.account);
    this.isAuthenticated.set(true);
    const redirectPath = this.tenantUrlUtil.getRedirectPath();
    const navigationTarget = redirectPath || "/orders";
    this.router.navigateByUrl(navigationTarget);
  }

  private handleAuthError(error: any): void {
    if (
      error?.code === "auth/popup-closed-by-user" ||
      error?.code === "auth/popup-blocked" ||
      error?.code === "auth/internal-error"
    ) {
      return;
    }

    const errorMessage = this.getAuthErrorMessage(error);
    this.error.set(errorMessage);
  }

  private getAuthErrorMessage(error: any): string {
    if (error?.error?.errorCode === "PRODUCT_ACCESS_DENIED") {
      return "Ponte en contacto con el administrador de tu empresa";
    }

    switch (error?.code) {
      case "auth/user-not-found":
      case "auth/wrong-password":
        return "Credenciales inválidas";
      case "auth/too-many-requests":
        return "Demasiados intentos fallidos. Intente más tarde";
      case "auth/network-request-failed":
        return "Error de conexión. Verifique su internet";
      case "auth/account-exists-with-different-credential":
        return "La cuenta ya existe con otro proveedor";
      default:
        return "Error de autenticación";
    }
  }

  private getPasswordResetErrorMessage(error: any): string {
    switch (error?.code) {
      case "auth/user-not-found":
        return "No existe una cuenta con este correo electrónico";
      case "auth/invalid-email":
        return "El correo electrónico no es válido";
      default:
        return "Error al enviar el correo de recuperación";
    }
  }

  private saveAuthData(account: AccountSummary): void {
    this.settings.set(SETTINGS_KEYS.AUTH_USER, account);
  }

  private loadAuthData(): void {
    const account = this.settings.get<AccountSummary>(SETTINGS_KEYS.AUTH_USER);
    if (!account) return;

    this.account.set(account);
    this.isAuthenticated.set(true);

    if (this.router.url.startsWith("/login")) {
      const redirectPath = this.tenantUrlUtil.getRedirectPath();
      const navigationTarget = redirectPath || "/";
      this.router.navigateByUrl(navigationTarget);
    }
  }

  clearAuthData(): void {
    this.settings.remove(SETTINGS_KEYS.AUTH_USER);
    this.account.set(null);
    this.isAuthenticated.set(false);
    this.error.set(null);
    this.queryClient.clear();
    this.queryClient.cancelQueries();
    this.queryClient.removeQueries();
    this.queryClient.invalidateQueries();
    this.queryClient.resetQueries();
  }

  hasPermissionFor(permissionId: string): boolean {
    const account = this.account();
    return account?.permissions?.some((p) => p === permissionId) ?? false;
  }

  hasPermissionsFor(permissionIds: string[]): boolean {
    const account = this.account();
    return (
      permissionIds.every((id) =>
        account?.permissions?.some((p) => p === id),
      ) ?? false
    );
  }

  canAccessFeature(modulePrefix: string): boolean {
    const account = this.account();
    return (
      account?.permissions?.some((p) => p.startsWith(modulePrefix)) ?? false
    );
  }

  resetAllMutations(): void {
    this.loginMutation.reset();
    this.loginWithMicrosoftMutation.reset();
    this.logoutMutation.reset();
    this.forgotPasswordMutation.reset();
  }
}
